import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  isNonJobExpense,
  jobMatchStrategyLabel,
  stFilingTarget,
} from "@/accounts-payable/utils";
import { FieldWithAction } from "./field-with-action";
import { ManualJobMatchButton } from "./manual-job-match";
import { ConfidenceLabel } from "./shared";
import { StFilingTargetLink } from "./st-filing-target-link";

export const MatchedJobField = ({
  document,
  businessId,
}: {
  document: ApPurchaseDocument;
  businessId: string | null;
}) => {
  // A job, or the project a document is filed on when it has no job.
  const target = stFilingTarget(document);
  const canMatch = canManuallyMatchJob(document);
  const nonJob = isNonJobExpense(document);

  return (
    <FieldWithAction
      label={
        target?.kind === "project"
          ? "Matched project"
          : document.document_type === "invoice"
            ? "ST invoice / job"
            : "Matched job"
      }
      action={
        canMatch && (
          <ManualJobMatchButton
            appearance="link"
            businessId={businessId}
            documentId={document.id}
            documentType={document.document_type}
            agentRunId={document.agent_run_id}
            label={target ? "Change job or project" : undefined}
          />
        )
      }
    >
      {target ? (
        <Stack align="start" gap={1}>
          <StFilingTargetLink
            document={document}
            documentType={document.document_type}
            className="ml-0 w-fit text-lg"
          />
          <MatchDetail document={document} />
        </Stack>
      ) : nonJob ? (
        <Text>No job</Text>
      ) : (
        <Text tone="muted">Not matched</Text>
      )}
    </FieldWithAction>
  );
};

const MatchDetail = ({ document }: { document: ApPurchaseDocument }) => {
  const strategy = document.job_match_strategy;
  const detail = jobMatchStrategyLabel(strategy);
  const confidence = document.job_match_confidence;
  if (!detail && confidence == null) return null;

  return (
    <Stack direction="row" gap={1} align="center" className="flex-wrap">
      {detail && (
        <Text variant="caption" tone="muted">
          {detail}
        </Text>
      )}
      {detail && confidence != null && (
        <Text variant="caption" tone="muted" aria-hidden>
          ·
        </Text>
      )}
      {/* Same figure as the Invoices table's job column. */}
      {confidence != null && <ConfidenceLabel value={confidence} />}
    </Stack>
  );
};
